import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/// The blocks' default titles and the tabs' default names in English, as the Home page settings store them until the owner
/// writes their own (HomeShelves): left as these, a page shows its own labels for them instead, in the page's language.
export const DEFAULT_BLOCK_TITLES: { [block: string]: string } = {
    'fresh-finds': 'Fresh Finds, Trending Styles – Start Exploring',
    'spotlight': 'Discover Top-Quality Car & Bike Accessories for Every Drive',
    'premium': 'Premium And Luxury Collections',
};
export const DEFAULT_TAB_NAMES: { [shelf: string]: string } = {
    'latest': 'Latest', 'super-saver': 'Super Saver', 'best-deals': 'Best Deals', 'luxury-deals': 'Luxury Deals',
    'mega-offers': 'Mega Offers', 'spotlight': 'Spotlight',
};

/// A home page block as the pages read it (Product/GetHomeBlocks): the owner's title and tab names, or the defaults.
export interface HomeBlock {
    Key: string;
    Title: string;
    /// The cards one tab draws.
    Shows: number;
    Tabs: HomeTab[];
}

export interface HomeTab {
    Shelf: string;
    Name: string;
}

/// A product in a bucket, a search result, or the product list's picker (HomeShelfAdmin).
export interface HomeShelfProduct {
    ProductId: number;
    Name: string;
    Slug: string;
    Sku: string;
    Image: string;
    Price: number;
    /// The home page can show it now: in stock and on the storefront.
    OnSale: boolean;
    /// "On sale", "Out of stock", "Not on the storefront" or "Deleted".
    State: string;
    AddedAt: string;
}

export interface HomeShelfAdminShelf {
    Key: string;
    Name: string;
    DefaultName: string;
    /// False for a block that is one shelf: it has only the block's title.
    HasTab: boolean;
    /// What fills what the bucket cannot.
    Rule: string;
    Items: HomeShelfProduct[];
}

export interface HomeShelfAdminBlock {
    Key: string;
    Title: string;
    DefaultTitle: string;
    Shows: number;
    Shelves: HomeShelfAdminShelf[];
}

export interface HomeShelfAnswer {
    Success: boolean;
    Message: string;
}

export interface HomeShelfMembership {
    Blocks: HomeBlock[];
    /// The shelves the product is in.
    In: string[];
}

/// The owner's Home page settings and the product list's picker, through /api/HomeShelf - owner only.
@Injectable({
    providedIn: 'root'
})
export class HomeShelfService {
    private readonly base = environment.apiUrl + '/api/HomeShelf';

    constructor(private http: HttpClient) { }

    admin(): Observable<HomeShelfAdminBlock[]> {
        return this.http.get<HomeShelfAdminBlock[]>(this.base + '/Admin');
    }

    forProduct(productId: number): Observable<HomeShelfMembership> {
        return this.http.get<HomeShelfMembership>(this.base + '/ForProduct/' + productId);
    }

    search(q: string): Observable<HomeShelfProduct[]> {
        return this.http.get<HomeShelfProduct[]>(this.base + '/Search', { params: { q } });
    }

    add(shelf: string, productId: number): Observable<HomeShelfAnswer> {
        return this.http.post<HomeShelfAnswer>(this.base + '/Add', { Shelf: shelf, ProductId: productId });
    }

    remove(shelf: string, productId: number): Observable<HomeShelfAnswer> {
        return this.http.post<HomeShelfAnswer>(this.base + '/Remove', { Shelf: shelf, ProductId: productId });
    }

    saveTexts(titles: { [block: string]: string }, tabs: { [shelf: string]: string }): Observable<HomeShelfAnswer> {
        return this.http.post<HomeShelfAnswer>(this.base + '/SaveTexts', { Titles: titles, Tabs: tabs });
    }
}
